import { Link } from 'react-router';
import { t } from '../i18n';

// Línea de tiempo (Gantt) sin bibliotecas: una fila por tarea, con su barra entre la fecha de inicio y la de
// vencimiento, la línea de hoy y las semanas o meses arriba. Las fechas son días (AAAA-MM-DD).

export interface GanttRow {
  id: string;
  label: string;
  /** Texto secundario (ej. la fase o el proyecto). */
  sublabel?: string;
  /** Sangría: 0 = fase, 1 = paso… */
  depth: number;
  start: string | null;
  end: string | null;
  tone: 'todo' | 'active' | 'done' | 'late' | 'cancelled';
  /** Fechas derivadas (ej. de una fase sin fechas propias, a partir de sus pasos): la barra se dibuja más tenue. */
  derived?: boolean;
  href?: string;
}

const DAY = 86_400_000;
const toTime = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
const toIso = (time: number) => new Date(time).toISOString().slice(0, 10);
const fmt = (iso: string, opts: Intl.DateTimeFormatOptions) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('es-PA', { ...opts, timeZone: 'UTC' });

const BAR = {
  todo: 'bg-slate-400',
  active: 'bg-marca',
  done: 'bg-green-600',
  late: 'bg-red-600',
  cancelled: 'bg-slate-300',
} as const;

export function Gantt({ rows, today, label }: { rows: readonly GanttRow[]; today: string; label: string }) {
  const dated = rows.filter((r) => r.start || r.end);
  if (dated.length === 0) return <p className="text-sm text-slate-500">{t('gantt.sin_fechas')}</p>;

  const times = dated.flatMap((r) => [r.start, r.end].filter((d): d is string => !!d).map(toTime));
  const from = Math.min(...times, toTime(today)) - 3 * DAY;
  const to = Math.max(...times, toTime(today)) + 3 * DAY;
  const days = Math.round((to - from) / DAY) + 1;
  // Escala según el largo del período: días visibles en proyectos cortos, semanas o meses en los largos.
  const px = days <= 70 ? 26 : days <= 200 ? 10 : 4;
  const width = days * px;
  const x = (iso: string) => ((toTime(iso) - from) / DAY) * px;

  // Marcas del encabezado: lunes en la escala de días o semanas, primer día del mes en la de meses.
  const ticks: Array<{ iso: string; label: string }> = [];
  for (let time = from; time <= to; time += DAY) {
    const iso = toIso(time);
    const d = new Date(time);
    if (px >= 10 ? d.getUTCDay() === 1 : d.getUTCDate() === 1) ticks.push({ iso, label: px >= 10 ? fmt(iso, { day: 'numeric', month: 'short' }) : fmt(iso, { month: 'short', year: '2-digit' }) });
  }

  return (
    <div className="overflow-x-auto rounded-md border border-slate-200" role="region" aria-label={label}>
      <div className="relative" style={{ width: `calc(16rem + ${width}px)` }}>
        <div className="flex border-b border-slate-200 bg-slate-50 text-xs text-slate-500">
          <div className="sticky left-0 z-10 w-64 shrink-0 border-r border-slate-200 bg-slate-50 px-3 py-1.5 font-semibold">{t('gantt.tarea')}</div>
          <div className="relative h-7" style={{ width }}>
            {ticks.map((tick) => (
              <span key={tick.iso} className="absolute top-1.5 whitespace-nowrap border-l border-slate-300 pl-1" style={{ left: x(tick.iso) }}>
                {tick.label}
              </span>
            ))}
          </div>
        </div>
        <ul>
          {rows.map((r) => {
            const start = r.start ?? r.end;
            const end = r.end ?? r.start;
            const title = r.start && r.end ? t('gantt.del_al', { desde: fmt(r.start, { day: 'numeric', month: 'short' }), hasta: fmt(r.end, { day: 'numeric', month: 'short' }) }) : end ? t('gantt.el', { fecha: fmt(end, { day: 'numeric', month: 'short' }) }) : t('gantt.sin_fecha');
            return (
              <li key={r.id} className="flex border-b border-slate-100 last:border-b-0">
                <div className="sticky left-0 z-10 w-64 shrink-0 border-r border-slate-200 bg-white px-3 py-1.5 text-sm" style={{ paddingLeft: `${0.75 + r.depth}rem` }}>
                  {r.href ? (
                    <Link to={r.href} className={`block truncate hover:underline ${r.depth === 0 ? 'font-medium text-slate-900' : 'text-slate-700'}`}>
                      {r.label}
                    </Link>
                  ) : (
                    <span className="block truncate">{r.label}</span>
                  )}
                  {r.sublabel && <span className="block truncate text-xs text-slate-500">{r.sublabel}</span>}
                </div>
                <div className="relative" style={{ width }}>
                  {/* Hoy */}
                  <span className="absolute inset-y-0 w-px bg-red-400" style={{ left: x(today) + px / 2 }} aria-hidden />
                  {start && end && (
                    <span
                      role="img"
                      aria-label={`${r.label}: ${title}`}
                      title={`${r.label}: ${title}`}
                      className={`absolute top-1/2 h-3 -translate-y-1/2 rounded ${BAR[r.tone]} ${r.derived ? 'opacity-40' : ''} ${!r.start ? 'h-3 w-3 rotate-45 rounded-none' : ''}`}
                      style={!r.start ? { left: x(end) + px / 2 - 6 } : { left: x(start), width: Math.max(x(end) - x(start) + px, 6) }}
                    />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      <p className="border-t border-slate-200 px-3 py-1.5 text-xs text-slate-500">{t('gantt.leyenda')}</p>
    </div>
  );
}
